"use client"

import { useTranslations } from "next-intl"
import { Package, Clock, CheckCircle, Truck, XCircle, RotateCcw } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { formatPrice } from "@/lib/utils"
import { cn } from "@/lib/utils"

interface OrderData {
  orderId?: string
  orderNumber?: string
  total?: number
  currency?: string | null
  status?: string
  date?: string
  itemCount?: number | null
  firstItemImage?: string | null
  firstItemTitle?: string | null
}

interface OrderMessageCardProps {
  order: OrderData
  variant?: "bubble" | "preview"
  isPending?: boolean
  onRemove?: () => void
}

const isUuid = (val?: string | null) => Boolean(val && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val))

export function OrderMessageCard({ 
  order, 
  variant = "bubble",
  isPending = false,
}: OrderMessageCardProps) {
  const tDetail = useTranslations("orders.detail")
  const tStatus = useTranslations("orders.statusBadge")
  const tItems = useTranslations("profile.tabs")
  if (!order) return null

  const getStatusDetails = (status: string) => {
    switch (status) {
      case "pending":
        return { color: "bg-warning/20 text-warning-foreground border-warning/30", icon: <Clock className="h-3 w-3 mr-1" /> }
      case "processing":
        return { color: "bg-warning/20 text-warning-foreground border-warning/30", icon: <Clock className="h-3 w-3 mr-1" /> }
      case "shipped":
        return { color: "bg-secondary/20 text-secondary-foreground border-secondary/30", icon: <Truck className="h-3 w-3 mr-1" /> }
      case "delivered":
        return { color: "bg-success/20 text-success-foreground border-success/30", icon: <CheckCircle className="h-3 w-3 mr-1" /> }
      case "cancelled":
        return { color: "bg-destructive/20 text-destructive-foreground border-destructive/30", icon: <XCircle className="h-3 w-3 mr-1" /> }
      case "refunded":
        return { color: "bg-muted/20 text-muted-foreground border-muted/30", icon: <RotateCcw className="h-3 w-3 mr-1" /> }
      default:
        return { color: "bg-warning/20 text-warning-foreground border-warning/30", icon: <Clock className="h-3 w-3 mr-1" /> }
    }
  }

  const displayStatus = order.status || "pending"
  const statusDetails = getStatusDetails(displayStatus)

  if (variant === "preview") {
    return (
      <div className={cn(
        "p-3 border rounded-lg bg-muted/20 transition-all duration-200",
        isPending && "opacity-70"
      )}>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gray-200 rounded-md flex items-center justify-center shrink-0 overflow-hidden">
            <Package className={cn(
              "h-6 w-6 text-muted-foreground transition-all duration-200",
              isPending && "opacity-50"
            )} />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-[10px] sm:text-xs truncate leading-tight">
              {tDetail("orderHash")}{order.orderNumber || '...'}
            </h4>
            <p className="text-primary font-semibold text-sm">
              {formatPrice(order.total || 0, order.currency || "USD")}
            </p>
            <p className="text-muted-foreground text-xs">
              {tStatus(order.status || 'pending')} • {order.date ? new Date(order.date).toLocaleDateString() : '...'}
            </p>
          </div>
          {isPending && (
            <div className="shrink-0">
              <div className="w-4 h-4 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
            </div>
          )}
        </div>
      </div>
    )
  }

  const displayNumber = order.orderNumber ?? (!isUuid(order.orderId) ? order.orderId : undefined) ?? "—"

  return (
    <div className="space-y-2 min-w-0">
      <div className="flex flex-wrap items-start gap-2 min-w-0">
        <div className="text-xs font-semibold min-w-0 flex-1 break-all">
          {tDetail("orderHash")}{displayNumber}
        </div>
        <Badge 
          variant="outline" 
          className={cn("text-xs border flex items-center shrink-0 whitespace-nowrap", statusDetails.color)}
        >
          {statusDetails.icon}
          <span>{tStatus(displayStatus)}</span>
        </Badge>
      </div>
      <div className="text-sm font-bold">{formatPrice(order.total || 0, order.currency || "USD")}</div>
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        {order.date && (
          <span>{new Date(order.date).toLocaleDateString()}</span>
        )}
        {order.itemCount && (
          <span>• {order.itemCount} {order.itemCount > 1 ? tItems("items") : tItems("item")}</span>
        )}
      </div>
    </div>
  )
}

